import { useState } from 'react';
import { useI18n } from '../contexts/I18nContext';
import { fetchPublicInstance } from '../lib/publicInstanceApi';
import { useInstanceStore } from '../store/useInstanceStore';

/**
 * Full-screen notice shown instead of the whole app while the Firmio Console
 * reports this instance as suspended. Nobody can sign in, admins included.
 * The retry button asks the server again, so access comes back without a hard
 * refresh once the instance is reactivated.
 */
export function SuspendedPage() {
  const { t } = useI18n();
  const message = useInstanceStore((s) => s.message);
  const clearSuspended = useInstanceStore((s) => s.clearSuspended);
  const [checking, setChecking] = useState(false);
  const [stillSuspended, setStillSuspended] = useState(false);

  async function retry() {
    setChecking(true);
    setStillSuspended(false);
    try {
      const info = await fetchPublicInstance();
      if (info.licenseStatus === 'active') {
        clearSuspended();
        window.location.assign('/login');
        return;
      }
      setStillSuspended(true);
    } catch {
      setStillSuspended(true);
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground">
      <div className="w-full max-w-md text-center">
        {/* Dark wordmark on the light theme, light wordmark on the dark one. */}
        <img
          src="/brand/firmio-logo-horizontal-dark.svg"
          alt="Firmio"
          className="mx-auto h-14 w-auto dark:hidden"
        />
        <img
          src="/brand/firmio-logo-horizontal-on-dark.svg"
          alt="Firmio"
          className="mx-auto hidden h-14 w-auto dark:block"
        />
        <div className="mt-10 rounded-2xl border border-border bg-card p-8 text-card-foreground shadow-xl">
          <h1 className="text-xl font-semibold">{t('instance.suspended.title')}</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground/80">
            {message || t('instance.suspended.defaultMessage')}
          </p>
          <p className="mt-4 text-sm text-muted-foreground">{t('instance.suspended.contact')}</p>
          <button
            type="button"
            onClick={() => void retry()}
            disabled={checking}
            className="mt-6 inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {checking ? t('instance.suspended.checking') : t('instance.suspended.retry')}
          </button>
          {stillSuspended && (
            <p className="mt-3 text-xs text-muted-foreground">{t('instance.suspended.still')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
